import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, forkJoin, Observable} from 'rxjs';
import {catchError, finalize, map, switchMap, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {DATE_MAX, DATE_MIN} from '../../../../../app.constants';
import {Destroyable} from '../../../../../destroyable.component';
import {ProfileType} from '../../../../employees/contract/contract.interfaces';
import {EmployeesService} from '../../../../employees/employees.service';
import {FormDescriptorService} from '../../../../form-generator/form-descriptor.service';
import {FormDescriptor, GroupZoneDescriptor, ZoneValues} from '../../../../form-generator/form-generator.interfaces';
import {LocalizationService} from '../../../../localization/localization.service';
import {FormField, TaskDescriptionType, Variable} from '../../../workflow-v2.interface';
import {
  NEW_ONBOARDING_TASK_DEFINITION_KEY,
  NEW_ONBOARDING_WORKFLOW_TYPE,
  ONBOARDING_TASK_DEFINITION_KEY
} from '../../../workflow-v2.service';

const isManagerWhoValidateTask = (variables) => variables.find(variable => variable.name === 'tree')?.value === 'HierarchicalOrganization';

@Component({
  selector: 'gp-form-descriptor-element',
  templateUrl: './form-descriptor-element.component.html',
  styleUrls: ['./form-descriptor-element.component.scss']
})
export class FormDescriptorElementComponent extends Destroyable implements OnInit {

  @Input()
  public cliRegId: number;

  @Input()
  public requestVariable: Variable;

  @Input()
  public contractId: number;

  @Input()
  public taskDescriptionField: FormField;

  @Input()
  public taskDescriptionType: TaskDescriptionType;

  @Input()
  public taskDefinitionKey: string;

  @Output()
  public errorTriggered: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public loadingEnded: EventEmitter<boolean> = new EventEmitter<boolean>();


  public groupValues: ZoneValues[] = [];
  public existingGroupValues: ZoneValues[];
  public groupZoneDescriptors: GroupZoneDescriptor[] = [];
  public loading = true;

  constructor(private employeesService: EmployeesService,
              private translateService: TranslateService,
              private localizationService: LocalizationService,
              private changeDetectorRef: ChangeDetectorRef,
              private formDescriptorService: FormDescriptorService) {
    super();
  }

  ngOnInit(): void {
    this.getValuesAndDescriptors();
  }

  public getValuesAndDescriptors() {
    if (this.isOnboardingTask()) {
      if (this.taskDefinitionKey === ONBOARDING_TASK_DEFINITION_KEY) {
        this.getOnboardingDescriptorsAndValues$().subscribe();
      } else {
        this.getNewOnboardingDescriptorsAndValues$().subscribe();
      }
    } else {
      this.groupValues.push(this.requestVariable.value);
      this.getExistingGroupValues().pipe(
        switchMap(() => this.getGroupZoneDescriptor(
              this.cliRegId,
              this.requestVariable.value.groupName,
              isManagerWhoValidateTask(this.taskDescriptionField.taskVariables) ? ProfileType.MSS : null)
        ),
        finalize(() => {
          this.loading = false;
          this.loadingEnded.emit(true);
        })
      ).subscribe();
    }
  }

  public getExistingGroupValues() {
    return this.employeesService.getContractDataForTasks(
      this.contractId, isManagerWhoValidateTask(this.taskDescriptionField.taskVariables) ? ProfileType.MSS : null)
      .pipe(
        tap(contract =>
          this.existingGroupValues = contract?.attributes?.empDatas?.filter(data => data.groupName === this.requestVariable.value.groupName)
            .sort((a, b) => moment(b.begindate).diff(moment(a.begindate)))),
        catchError(error => {
          if (error.status === 404) {
            this.errorTriggered.emit('NOTIFICATIONS.no_employee_data_access');
          }
          return EMPTY;
        })
      );
  }

  public getGroupZoneDescriptor(cliRegId: number, groupZoneId: string, profileType: ProfileType): Observable<GroupZoneDescriptor> {
    return this.formDescriptorService.getFormDescriptorGroupZoneID(cliRegId, groupZoneId, profileType)
      .pipe(
        map((formDescriptor: FormDescriptor) => formDescriptor.tabSheets[0]?.groupZones[0]),
        tap(groupDescriptor => {
          if (!groupDescriptor) {
            this.errorTriggered.emit('NOTIFICATIONS.no_employee_data_access');
          } else {
            this.groupZoneDescriptors.push(groupDescriptor);
            this.addGroupZoneDescriptorToValues();
            if (!groupDescriptor.isDated) {
              this.taskDescriptionField.value += '_not_dated';
            }
            this.changeDetectorRef.markForCheck();
          }
        }),
      );
  }

  private addGroupZoneDescriptorToValues() {
    this.groupValues.forEach(value => {
      value.groupDescriptor = this.getGroupZoneDescriptorByName(value.groupName);
      if (!value.contractId) {
        value.contractId = this.contractId;
      }
    });
    this.groupValues.sort((a, b) =>
      (a.groupDescriptor?.tabSheetPosition - b.groupDescriptor?.tabSheetPosition)
      || (a.groupDescriptor?.displayorder - b.groupDescriptor?.displayorder));
    if (this.existingGroupValues) {
      this.existingGroupValues.forEach(value => {
        value.groupDescriptor = this.getGroupZoneDescriptorByName(value.groupName);
        value.validRangeLabel = this.getValidRangeLabel(value.begindate, value.enddate);
      });
    }
  }

  private getGroupZoneDescriptorByName(groupName: string): GroupZoneDescriptor {
    return this.groupZoneDescriptors.find(group => group.groupZoneID === groupName);
  }

  public isOnboardingTask(): boolean {
    const workflowType = this.taskDescriptionField.taskVariables?.find(variable => variable.name === 'type')?.value;
    return this.taskDefinitionKey === ONBOARDING_TASK_DEFINITION_KEY
        || (workflowType === NEW_ONBOARDING_WORKFLOW_TYPE && this.taskDefinitionKey === NEW_ONBOARDING_TASK_DEFINITION_KEY);
  }

  private getOnboardingDescriptorsAndValues$(): Observable<([GroupZoneDescriptor[], ZoneValues[]])> {
    return forkJoin([this.getOnboardingFormDescriptor(this.cliRegId), this.getOnboardingValues(this.contractId)]).pipe(
      tap(([groupDescriptors, groupValues]) => {
        this.groupZoneDescriptors = groupDescriptors;
        this.groupValues = groupValues;
        this.addGroupZoneDescriptorToValues();
        this.changeDetectorRef.markForCheck();
      }),
    finalize(() => {
      this.loading = false;
      this.loadingEnded.emit(true);
    })
    );
  }

  private getNewOnboardingDescriptorsAndValues$(): Observable<([GroupZoneDescriptor[], ZoneValues[]])> {
    return forkJoin([this.getNewOnboardingFormDescriptor(this.cliRegId), this.getNewOnboardingValues(this.contractId)]).pipe(
      tap(([groupDescriptors, groupValues]) => {
        this.groupZoneDescriptors = groupDescriptors;
        this.groupValues = groupValues;
        this.addGroupZoneDescriptorToValues();
        this.changeDetectorRef.markForCheck();
      }),
      finalize(() => {
        this.loading = false;
        this.loadingEnded.emit(true);
      })
    );
  }

  public getOnboardingFormDescriptor(cliReg: number): Observable<GroupZoneDescriptor[]> {
    return this.formDescriptorService.getOnboardingFormDescriptor(cliReg).pipe(
      map((formDescriptor: FormDescriptor) => formDescriptor.tabSheets.flatMap(tabsheet =>
        tabsheet.groupZones.map(groupZone => ({...groupZone, tabSheetPosition: tabsheet.tabSheetPosition})))));
  }

  public getNewOnboardingFormDescriptor(cliReg: number): Observable<GroupZoneDescriptor[]> {
    return this.formDescriptorService.getNewOnboardingFormDescriptor(cliReg).pipe(
      map((formDescriptor: FormDescriptor) => formDescriptor.tabSheets.flatMap(tabsheet =>
        tabsheet.groupZones.map(groupZone => ({...groupZone, tabSheetPosition: tabsheet.tabSheetPosition})))));
  }

  public getOnboardingValues(contractId: number): Observable<ZoneValues[]> {
    return this.employeesService.getOnboardingValues(contractId);
  }

  public getNewOnboardingValues(contractId: number): Observable<ZoneValues[]> {
    return this.employeesService.getNewOnboardingValues(contractId);
  }

  private getValidRangeLabel(begin: string, end: string): string {
    let label: string;

    if (DATE_MIN === begin && DATE_MAX === end) {
      label = 'EMPLOYEE_MANAGE.Valid_from_begin_to_end';
    } else if (DATE_MIN === begin && DATE_MAX !== end) {
      label = 'EMPLOYEE_MANAGE.Valid_from_begin_to_date';
    } else if (DATE_MIN !== begin && DATE_MAX === end) {
      label = 'EMPLOYEE_MANAGE.Valid_from_date_to_end';
    } else {
      label = 'EMPLOYEE_MANAGE.Valid_from_to';
    }

    return this.translateService.instant(label, {
      validFrom: moment(begin).format(this.localizationService.dateFormat),
      validTill: moment(end).format(this.localizationService.dateFormat)
    });

  }
}
